import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tag } from '../../UI';
import { Button } from '../../UI/base/Button';
import { DateField } from '../../UI/base';
import type { RecurrenceDaysOfWeekEnum } from '../../../api/generated';
import { isOccurrenceDate } from './bookingTime';
import styles from './BookingSkipDatesField.module.css';

interface BookingSkipDatesFieldProps {
  startDate: string;
  daysOfWeek: RecurrenceDaysOfWeekEnum[];
  intervalWeeks: number;
  endDate: string;
  exceptionDates: string[];
  onChange: (dates: string[]) => void;
}

export const BookingSkipDatesField = ({
  startDate,
  daysOfWeek,
  intervalWeeks,
  endDate,
  exceptionDates,
  onChange
}: BookingSkipDatesFieldProps) => {
  const { t } = useTranslation();
  const [candidate, setCandidate] = useState('');
  const [error, setError] = useState('');

  const handleAdd = () => {
    if (!candidate) {
      setError(t('booking-skip-error-empty'));
      return;
    }
    if (exceptionDates.includes(candidate)) {
      setError(t('booking-skip-error-duplicate'));
      return;
    }
    if (!isOccurrenceDate(candidate, startDate, daysOfWeek, intervalWeeks, endDate || undefined)) {
      setError(t('booking-skip-error-not-occurrence'));
      return;
    }
    // ISO date strings sort chronologically, so the chips stay in date order.
    onChange([...exceptionDates, candidate].sort((a, b) => a.localeCompare(b)));
    setCandidate('');
    setError('');
  };

  const handleRemove = (date: string) => {
    onChange(exceptionDates.filter((d) => d !== date));
  };

  return (
    <div className={styles.skipField}>
      <span className={styles.label}>{t('booking-skip-label')}</span>
      <div className={styles.addRow}>
        <DateField
          value={candidate}
          error={Boolean(error)}
          helperText={error || t('booking-skip-hint')}
          onChange={(date) => {
            setCandidate(date);
            setError('');
          }}
        />
        <Button type="secondary" onClick={handleAdd}>
          {t('booking-skip-add')}
        </Button>
      </div>
      {exceptionDates.length > 0 && (
        <div className={styles.chips}>
          {exceptionDates.map((date) => (
            <Tag
              key={date}
              label={date}
              onRemove={() => handleRemove(date)}
              removeAriaLabel={t('booking-skip-remove', { date })}
            />
          ))}
        </div>
      )}
    </div>
  );
};
